import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, Paperclip, Upload, X } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';

const ACCEPT = '.jpg,.jpeg,.png,.gif,.webp,.heic,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const kb = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

/** Files are served through the API so the capability check still applies —
 *  fetch as a blob rather than pointing an href at the backend. */
export const openAttachment = async (att) => {
  try {
    const res = await api.get(`/gsoc/attachments/${att.id}/download`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    return true;
  } catch {
    toast.error(`Could not open ${att.filename}`);
    return false;
  }
};

export function AttachmentChips({ items = [], onRemove, onOpened, openedIds, testIdPrefix = 'attachment' }) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-list`}>
      {items.map((a) => {
        const seen = openedIds?.has(a.id);
        return (
        <span key={a.id}
              className={`inline-flex items-center gap-2 rounded-full border pl-3 pr-2 py-1 text-sm ${seen
                ? 'border-[hsl(var(--success))] bg-success-bg' : 'border-line bg-surface-sunken'}`}>
          <button type="button" onClick={async () => { if (await openAttachment(a)) onOpened?.(a); }}
                  className="inline-flex items-center gap-1.5 text-fg hover:underline"
                  data-testid={`${testIdPrefix}-open-${a.id}`}>
            {seen
              ? <Check className="w-3.5 h-3.5 shrink-0 text-success" aria-hidden="true" />
              : <Paperclip className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />}
            <span className="max-w-[220px] truncate">{a.filename}</span>
            <span className="text-xs text-fg-muted">{kb(a.size || 0)}</span>
          </button>
          {onRemove && (
            <button type="button" onClick={() => onRemove(a)} aria-label={`Remove ${a.filename}`}
                    className="text-fg-muted hover:text-danger" data-testid={`${testIdPrefix}-remove-${a.id}`}>
              <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          )}
        </span>
        );
      })}
    </div>
  );
}

export function AttachmentUpload({ noteId, onUploaded, testId = 'attachment-upload' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await api.post(`/gsoc/pass-on/${noteId}/attachments`, form,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} attached`);
      onUploaded?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not attach ${file.name}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={ACCEPT} className="hidden" onChange={pick}
             data-testid={`${testId}-input`} />
      <Button variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()}
              data-testid={`${testId}-btn`}>
        <Upload className="w-4 h-4 mr-1" aria-hidden="true" />
        {busy ? 'Uploading...' : 'Attach File'}
      </Button>
    </>
  );
}
